<template>
  <div class="document-uploader-dialog">
    <vs-prompt
      id="document-uploader-dialog"
      @accept="acceptAddDocument"
      @cancel="cancel"
      @close="close"
      :color="AISEA_COLOR.BLUE"
      :active.sync="showDialog"
      :title="title"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :is-valid="validateForm"
      :buttons-hidden="buttonsHidden"
    >
      <div>
        <p>ファイルの選択</p>
        <div
          v-if="!hasDocuments"
          :class="[isDrag ? dragoverClass : dragleaveClass]"
          @dragover.prevent="checkDrag($event, true)"
          @dragleave.prevent="checkDrag($event, false)"
          @drop.prevent="handleFileSelect($event)"
          @click="$refs.update_photo_input.click()"
        >
          <input
            type="file"
            ref="update_photo_input"
            class="hidden"
            @change="handleFileSelect($event)"
            multiple
          />
          <span
            >ファイルをドロップ、<br />またはクリックしてファイルを選択<br />
            <P class="text-sm mt-2"
              >最大アップロードサイズ：<strong>50MB</strong></P
            ></span
          >
        </div>
        <div v-else>
          <VuePerfectScrollbar
            class="scroll-area mt-2"
            :settings="scrollbarSettings"
          >
            <div
              v-for="(file, index) in documents"
              :key="index"
              class="file-name-label relative p-2"
            >
              <!-- 書類名ラベルエリア -->
              <div
                class="flex items-center justify-between"
                v-on:mouseover="mouseOverFile(file)"
                v-on:mouseleave="mouseLeaveFile(file)"
              >
                <div class="flex items-center">
                  <document-icon
                    svgClasses="h-6 w-6"
                    :type="getFileType(file.type)"
                    class="mr-2"
                  ></document-icon>
                  <div v-show="!file.isEditing" class="break-all">
                    {{ file.name }}
                  </div>

                  <!-- 編集中は入力欄を表示 -->
                  <vs-input
                    v-show="file.isEditing"
                    v-model="editNameText"
                    @input="validateEditingFileName(file)"
                    :class="isValidEditName"
                  />
                </div>

                <!-- 編集中は変更ボタン、キャンセルボタンを表示 -->
                <div v-show="file.isEditing" class="flex items-center">
                  <!-- バリデートエラーがない時のみ変更ボタンを押下可能 -->
                  <div v-if="isValidEditName(file)">
                    <vx-tooltip text="名前を変更">
                      <feather-icon
                        class="action-button-icon"
                        icon="CheckIcon"
                        svgClasses="w-5 h-5"
                        @click="commitChangeFileName(file)"
                      ></feather-icon>
                    </vx-tooltip>
                  </div>
                  <div v-else>
                    <feather-icon
                      class="action-button-icon action-button-icon__disabled"
                      icon="CheckIcon"
                      svgClasses="w-5 h-5"
                    ></feather-icon>
                  </div>

                  <vx-tooltip text="キャンセル">
                    <feather-icon
                      class="action-button-icon"
                      icon="XIcon"
                      svgClasses="w-5 h-5"
                      @click="cancelChangeFileName(file)"
                    ></feather-icon>
                  </vx-tooltip>
                </div>

                <!-- ホバー時に表示するエリア -->
                <div
                  class="hover-area"
                  v-show="file.isHovered && !hasEditingFile"
                >
                  <div class="absolute top-0 right-0">
                    <div class="flex items-center mr-2">
                      <!-- ファイル名編集ボタン -->
                      <vx-tooltip text="名前を編集">
                        <feather-icon
                          class="action-button-icon"
                          icon="EditIcon"
                          svgClasses="w-5 h-5"
                          @click="editFileName(file)"
                        ></feather-icon>
                      </vx-tooltip>
                      <!-- 削除ボタン -->
                      <vx-tooltip text="削除">
                        <feather-icon
                          class="action-button-icon"
                          icon="TrashIcon"
                          svgClasses="w-5 h-5"
                          @click="deleteFile(file)"
                        ></feather-icon>
                      </vx-tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- エラーの場合はエラーメッセージを表示 -->
              <span
                class="text-danger text-sm ml-8"
                v-show="errors.has(file.field) && !file.isEditing"
                >{{ errors.first(file.field) }}</span
              >
              <span
                class="text-danger text-sm ml-8"
                v-show="errors.has(`${file.field}_edit`) && file.isEditing"
                >{{ errors.first(`${file.field}_edit`) }}</span
              >

            </div>
          </VuePerfectScrollbar>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import "cropperjs/dist/cropper.css";
import { FileUtil } from "@/utils";
import { createNamespacedHelpers } from "vuex";
import { ColorMixin, DocumentMixin } from "@/mixins";
import _ from "lodash"
import { TASK_MANAGER_CATEGORY, TASK_MANAGER_STATUS } from "@/config/const";
import DocumentIcon from "../icon/DocumentIcon";
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

const {
  mapActions: mapActionsOfEnterpriseDocumentManager,
  mapState: mapStateOfEnterpriseDocumentManager
} = createNamespacedHelpers("enterpriseDocumentManager");

const {
  mapActions: mapActionsOfBgTaskManager,
} = createNamespacedHelpers("enterpriseBackgroundTaskManager");

const MAXIMUM_FILE_SIZE = 50 * 1024 * 1024; //50MB


export default {
  components: { DocumentIcon, VuePerfectScrollbar },
  mixins: [ColorMixin, DocumentMixin],
  data () {
    return {
      showDialog: false,
      title: "船舶書類の追加",
      acceptText: "追加",
      cancelText: "キャンセル",
      buttonsHidden: false,
      dragleaveClass: "drag-area-dragleave",
      dragoverClass: "drag-area-dragover",
      documents: [],

      // ファイル選択エリア
      isDrag: false,
      isCrop: null,

      // ファイル名編集
      editNameText: "",

      // タスクマネージャー
      task: null,
    };
  },
  computed: {
    ...mapStateOfEnterpriseDocumentManager(["vesselId"]),
    validateForm () {
      return (
        this.documents.length > 0 &&
        !this.errors.any()
      );
    },
    hasDocuments () {
      return this.documents.length > 0;
    },

    hasEditingFile () {
      return this.documents.filter(file => file.isEditing).length > 0;
    },

    editingFile(){
      return this.documents.find(file => file.isEditing);
    },

    validateEditNameForm () {
      return this.validateEditNameRequired && 
        this.validateEditNameLength && 
        this.validateEditNameSlash;
    },

    validateEditNameRequired () {
      return this.editNameText.length > 0
    },

    validateEditNameLength () {
      return this.editNameText.length <= 255
    },

    validateEditNameSlash () {
      return !this.editNameText.includes("/")
    }
  },
  watch: {
    documents (val) {
      if (val.length === 0) {
        this.removeAllErrors();
      }
    },
  },
  methods: {
    ...mapActionsOfEnterpriseDocumentManager([
      "getCurrentPath",
      "resetViewState",
      "reloadDocuments",
      "fetchRootSidebarFolders"
    ]),
    ...mapActionsOfBgTaskManager(["startUploadTask"]),

    show () {
      this.showDialog = true;
    },

    isValidEditName (file) {
      if (file.name === this.editNameText) return false;
      return this.validateEditNameForm && this.validateLocalFileName(file) && !this.errors.any(`${file.field}_edit`);
    },

    validateLocalFileName (file) {
      if (file.name === this.editNameText) return true;
      return this.documents.filter(item => {
        return item.name === this.editNameText
      }).length < 1;
    },

    async validateEditingFileName (file) {
      const fieldName = `${file.field}_edit`;
      this.$validator.errors.remove(fieldName);
      if (!this.validateEditNameRequired) {
        this.$validator.errors.add({
          field: fieldName,
          msg: `名前を入力してください`
        })
        return;
      }

      if (!this.validateEditNameLength) {
        this.$validator.errors.add({
          field: fieldName,
          msg: `名前は255文字以内にしてください`
        })
        return;
      }

      if (!this.validateLocalFileName(file)) {
        this.$validator.errors.add({
          field: fieldName,
          msg: `既に存在するファイル名です`
        })
        return;
      }

      const payload = {
        name: this.editNameText,
        field: fieldName,
      }

      await this.validateSlash(payload);
      await this._validateFileName(this, payload);

    },


    async validateFileName (file) {
      this.$validator.errors.remove(file.field);
      if (!this.validateLocalFileName(file)) {
        this.$validator.errors.add({
          field: file.field,
          msg: `既に存在するファイル名です`
        })
        return;
      }

      const payload = {
        name: file.name,
        field: file.field,
      };

      await this.validateSlash(payload);
      await this.validateExistsFileName(payload)

    },


    _validateFileName: _.debounce(async (self, file) => {
      const payload = {
        name: file.name,
        field: file.field,
      }

      await self.validateExistsFileName(payload)

    }, 100),


    validateFileSize(file) {
      if(file.size > MAXIMUM_FILE_SIZE) {
        this.$validator.errors.remove(file.field);
        this.$validator.errors.add({
          field: file.field,
          msg: `最大アップロードサイズ50MBを超えてます`
        })
      }
    },

    getFileType (type) {
      return this.getDocumentMimeKind(type)
    },

    async acceptAddDocument(){
      if(this.hasEditingFile) {
        await this.commitChangeFileName(this.editingFile)
      }
      await this.uploadAllDocument();
    },

    async uploadAllDocument () {
      const promiseAll = [];

      this.documents.forEach(item => {
        promiseAll.push(this.uploadDocument(item));
      })

      Promise.all(promiseAll);
      this.close();

      const task = {
        name: "",
        type: TASK_MANAGER_CATEGORY.UPLOAD,
        status: TASK_MANAGER_STATUS.PROGRESS
      };

      this.$vs.notify({
        title: `船舶書類アップロード`,
        text: `書類のアップロードを開始しました。進捗状況はタスクマネージャーにて確認できます。`,
        color: "success",
        iconPack: "feather",
        icon: "icon-check",
        click: () => { this.clickNotify(task) },
        time: 5000,
      });
    },

    async uploadDocument (file) {
      let fileSrc = FileUtil.toBlob(file.src, file.type);
      const path = `${await this.getCurrentPath()}${file.name}`;

      const task = {
        name: file.name,
        type: TASK_MANAGER_CATEGORY.UPLOAD,
        status: TASK_MANAGER_STATUS.PROGRESS
      }

      let payload = {
        name: file.name,
        props: {
          bytes: file.size,
          contentType: file.type,
          vesselId: this.vesselId
        },
        path: path,
        file: fileSrc,
        completedFunc: () => { this.completedTask(task); }
      };

      await this.startUploadTask(payload);
    },

    completedTask (task) {
      this.$vs.notify({
        title: `船舶書類アップロード`,
        text: `書類「${task.name}」のアップロードが完了しました。`,
        color: "success",
        iconPack: "feather",
        icon: "icon-check",
        time: 5000,
        click: () => { this.clickNotify(task) }
      });
      this.resetViewState();
      this.reloadDocuments();
      this.fetchRootSidebarFolders();
    },

    clickNotify (task) {
      this.$router.push({
        name: "task-manager",
        params: { name: task.name, type: task.type, status: task.status }
      });
    },


    checkDrag (e, isDrag) {
      this.isDrag = isDrag;
    },

    handleFileSelect (e) {
      let files = [];

      if ("files" in e.target) {
        files = e.target.files;
      } else if ("files" in e.dataTransfer) {
        files = e.dataTransfer.files;
      }

      files.forEach((item) => {
        const file = {};

        file.name = item.name;
        file.size = item.size;

        const heicExtensionRegex = /\.heic$/i;
        if (item.type != "image/heic" && heicExtensionRegex.test(item.name)) {
          file.type = "image/heic";
        } else {
          file.type = item.type;
        }

        file.field = _.cloneDeep(`filename_${item.name}`);
        file.isHovered = false;
        file.isEditing = false;

        let reader = new FileReader();
        reader.onload = _e => {
          file.src = _e.target.result;
        };
        reader.readAsDataURL(item);

        this.validateExistsFileName(file);
        this.validateFileSize(file);

        this.documents.push(file);
      });
    },


    mouseOverFile (file) {
      file.isHovered = true;
    },
    mouseLeaveFile (file) {
      file.isHovered = false;
    },


    editFileName (file) {
      this.editNameText = _.cloneDeep(file.name);
      file.isEditing = true;
      this.validateEditingFileName(file);
    },

    commitChangeFileName (file) {
      this.$validator.errors.remove(`${file.field}`);
      this.$validator.errors.remove(`${file.field}_edit`);
      file.name = _.cloneDeep(this.editNameText);
      file.field = _.cloneDeep(`filename_${this.editNameText}`);

      file.isEditing = false;
      this.editNameText = "";
    },

    cancelChangeFileName (file) {
      this.$validator.errors.remove(`${file.field}_edit`);
      this.validateFileName(file);
      file.isEditing = false;
      this.editNameText = "";
    },

    deleteFile (file) {
      this.$validator.errors.remove(file.field);
      this.$validator.errors.remove(`${file.field}_edit`);
      this.documents = this.documents.filter(item => {
        return file !== item;
      });
    },

    close () {
      this.cleanUp();
    },

    cancel () {
      this.cleanUp();
    },

    removeAllErrors () {
      this.documents.forEach(file => {
        this.$validator.errors.remove(file.field);
        this.$validator.errors.remove(`${file.field}_edit`);
      });
    },

    removeTooltip () {
      //ツールチップが消えていない場合、消す。
      document.querySelectorAll('body > div.vs-tooltip').forEach(el => {
        if (document.body.contains(el)) {
          document.body.removeChild(el);
        }
      });
    },


    cleanUp () {
      this.removeAllErrors();
      this.$validator.pause();
      this.showDialog = false;
      this.documents = [];
      this.editNameText = "";
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.vs-tooltip {
  z-index: 52006;
}

#document-uploader-dialog {
  .scroll-area {
    max-height: 360px;
  }

  .file-name-label {
    .action-button-icon {
      border-radius: 4px;
      padding: 8px;
      margin: 0;
      margin-left: 6px;
      color: $aisea-gray;
      background-color: $aisea-white;
      cursor: pointer;

      &__disabled {
        color: $aisea-light-gray;
        background-color: $aisea-white;
        cursor: default;
      }
    }
  }

  span > canvas {
    height: 200px !important;
    width: auto !important;
  }
  .drag-area-dragleave {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px dotted rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }
  .drag-area-dragover {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px solid $aisea-blue;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }
}
</style>
